import { useEffect, useMemo, useState } from "react";
import { priceMotor } from "@fa/pricing";
import { motorRates } from "@fa/rates";
import { motorPayloadSchema, todayIso } from "@fa/shared";
import {
  DocumentUploadSection,
  Field,
  FormError,
  IdentitySection,
  PaymentStatus,
  QuoteTable,
  RatesDisclaimer,
  ReviewSummary,
  StepIndicator,
  StepNavigation,
  SubmitButton,
} from "../components/Shared";
import { useFormValidation } from "../hooks/useFormValidation";
import { useStepWizard, type StepDef } from "../hooks/useStepWizard";
import { initiateStk, listMemberDocuments, pollStkStatus, submit, submitDocs, useApiBase, type MemberDocument, type StkStatus } from "../api";

export function MotorForm() {
  const api = useApiBase();
  const [identity, setIdentity] = useState({
    icpakMemberNumber: "",
    firstName: "",
    lastName: "",
    phone: "",
    email: "",
  });
  const [vehicle, setVehicle] = useState({
    make: "",
    model: "",
    coverType: "COMPREHENSIVE" as "COMPREHENSIVE" | "THIRD_PARTY",
    vehicleValueKes: 0,
    registrationNumber: "",
    yearOfManufacture: new Date().getFullYear() - 1,
    coverStartDate: todayIso(),
  });
  const [ext, setExt] = useState({
    excessProtector: true,
    politicalViolenceAndTerrorism: true,
    aaRoadRescue: true,
    personalAccident: true,
    courtesyCar: true,
  });
  const [reference, setReference] = useState<string | null>(null);
  const [mpesaError, setMpesaError] = useState<string | null>(null);
  const [stkStatus, setStkStatus] = useState<StkStatus | null>(null);
  const [stkReceipt, setStkReceipt] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isStkLoading, setIsStkLoading] = useState(false);
  const [isSubmittingDocs, setIsSubmittingDocs] = useState(false);
  const [docsSubmitted, setDocsSubmitted] = useState(false);
  const [existingDocs, setExistingDocs] = useState<MemberDocument[]>([]);
  const [payLater, setPayLater] = useState(false);
  const [vehicleAgeError, setVehicleAgeError] = useState<string | null>(null);

  // Fetch existing documents from SharePoint using phone as the stable folder identifier
  useEffect(() => {
    if (identity.phone) {
      listMemberDocuments(api, identity.phone).then(setExistingDocs).catch(() => {});
    }
  }, [identity.phone, api]);

  const payload = useMemo(
    () => ({ ...identity, ...vehicle, extensions: ext }),
    [identity, vehicle, ext],
  );

  const { errors, allErrors, isValid, parsed, touch, touchAll } = useFormValidation(
    motorPayloadSchema,
    payload,
  );
  const quote = isValid && parsed ? priceMotor(parsed, motorRates) : null;
  const payableNowKes = quote ? Math.ceil(quote.totalKes * 0.3) : 0;

  const steps: StepDef[] = useMemo(
    () => [
      {
        key: "identity",
        label: "Member Details",
        validate: () => {
          const fields = ["icpakMemberNumber", "firstName", "lastName", "phone", "email"];
          const hasErrors = fields.some((f) => f in allErrors);
          if (hasErrors) {
            fields.forEach(touch);
            return false;
          }
          return true;
        },
      },
      {
        key: "vehicle",
        label: "Vehicle Details",
        validate: () => {
          const fields = ["make", "model", "coverType", "registrationNumber", "yearOfManufacture"];
          if (vehicle.coverType === "COMPREHENSIVE") fields.push("vehicleValueKes");
          const hasErrors = fields.some((f) => f in allErrors);
          if (hasErrors) {
            fields.forEach(touch);
            return false;
          }
          const age = new Date().getFullYear() - vehicle.yearOfManufacture;
          if (age > 15) {
            setVehicleAgeError(
              "This vehicle exceeds the maximum age of 15 years. Vehicles above 15 years can be considered on mechanical evaluation — please contact us directly.",
            );
            return false;
          }
          setVehicleAgeError(null);
          return true;
        },
      },
      {
        key: "extensions",
        label: "Extensions & Cover",
        validate: () => {
          if ("coverStartDate" in allErrors) {
            touch("coverStartDate");
            return false;
          }
          return true;
        },
      },
      {
        key: "review",
        label: "Review & Submit",
      },
    ],
    [allErrors, touch, vehicle.coverType, vehicle.yearOfManufacture],
  );

  const wizard = useStepWizard(steps);

  async function submitDetails() {
    if (!isValid) { touchAll(); return; }
    setIsSubmitting(true);
    setSubmitError(null);
    try {
      const res = await submit(api, "motor_private", parsed!);
      setReference(res.reference);
    } catch (e) {
      setSubmitError((e as Error).message);
    } finally {
      setIsSubmitting(false);
    }
  }

  async function triggerStk() {
    if (!reference) return;
    setMpesaError(null);
    setStkReceipt(null);
    setStkStatus("PENDING");
    setIsStkLoading(true);
    try {
      const r = await initiateStk(api, {
        reference,
        phoneNumber: identity.phone,
        amount: String(payableNowKes),
        accountNumber: vehicle.registrationNumber,
      });
      if (String(r.ResponseCode) !== "0") {
        setMpesaError(r.Description ?? "Check your phone for the M-Pesa prompt.");
      }
      // STK dispatched — stop showing the button as loading; status spinner takes over
      setIsStkLoading(false);
      const final = await pollStkStatus(api, reference, {
        intervalMs: 4000,
        timeoutMs: 180_000,
        onTick: (s) => {
          setStkStatus(s.status);
          if (s.receipt) setStkReceipt(s.receipt);
          if (s.status !== "PENDING" && s.status !== "UNKNOWN") setMpesaError(null);
        },
      });
      setStkStatus(final.status);
      if (final.receipt) setStkReceipt(final.receipt);
      if (final.status !== "PENDING" && final.status !== "UNKNOWN") setMpesaError(null);
    } catch (e) {
      setMpesaError("Could not send the M-Pesa prompt. Please try again or pay manually via Paybill.");
      setStkStatus(null);
      setIsStkLoading(false);
    }
  }

  async function handleSubmitDocs() {
    setIsSubmittingDocs(true);
    try {
      await submitDocs(api, reference!);
      setDocsSubmitted(true);
    } catch (e) {
      setSubmitError((e as Error).message);
    } finally {
      setIsSubmittingDocs(false);
    }
  }

  // After submission: show payment + docs
  if (reference) {
    return (
      <div className="fa-mt-6 fa-space-y-4">
        <div className="fa-rounded-md fa-bg-slate-50 fa-p-4">
          <p>
            Your reference is <b>{reference}</b>. Pay at least 30% of the
            Annual Premium via M-Pesa Paybill <b>898200</b>, Account{" "}
            <b>{vehicle.registrationNumber}</b> (
            <b>KES {payableNowKes.toLocaleString()}</b>).
          </p>
          <div className="fa-flex fa-flex-wrap fa-gap-2 fa-mt-2">
            <SubmitButton onClick={triggerStk} loading={isStkLoading} variant="orange">
              Send me an STK Push
            </SubmitButton>
            {!payLater && stkStatus !== "SUCCESS" && (
              <button
                type="button"
                className="fa-btn-primary fa-text-sm"
                onClick={() => setPayLater(true)}
              >
                Pay Later
              </button>
            )}
          </div>
          <PaymentStatus status={stkStatus} receipt={stkReceipt} error={mpesaError} />
          {payLater && stkStatus !== "SUCCESS" && (
            <p className="fa-text-xs fa-text-amber-700 fa-mt-2">
              You can pay at least 30% (<b>KES {payableNowKes.toLocaleString()}</b>) later via M-Pesa
              Paybill <b>898200</b>, Account <b>{vehicle.registrationNumber}</b>. Please upload your
              documents below.
            </p>
          )}
        </div>
        <DocumentUploadSection
          reference={reference}
          documents={[
            { kind: "ID", label: "ID photo (both sides)" },
            { kind: "LOGBOOK", label: "Logbook" },
            { kind: "KRA_PIN", label: "KRA PIN Certificate" },
          ]}
          requiredPayment={!payLater}
          paymentConfirmed={stkStatus === "SUCCESS"}
          onSubmitDocs={handleSubmitDocs}
          isSubmittingDocs={isSubmittingDocs}
          docsSubmitted={docsSubmitted}
          existingDocs={existingDocs}
        />
      </div>
    );
  }

  return (
    <div ref={wizard.containerRef}>
      <StepIndicator
        steps={steps}
        currentIndex={wizard.currentStepIndex}
        completedSteps={wizard.completedSteps}
        onStepClick={wizard.goTo}
      />

      {wizard.currentStep.key === "identity" && (
        <div className="fa-step-enter" key="identity">
          <IdentitySection value={identity} onChange={setIdentity} errors={errors} onBlur={touch} />
        </div>
      )}

      {wizard.currentStep.key === "vehicle" && (
        <div className="fa-step-enter" key="vehicle">
          <div className="fa-section fa-section-orange fa-space-y-3">
            <Field id="coverType" label="Select Preferred Cover" required>
              <select
                id="coverType"
                value={vehicle.coverType}
                onChange={(e) =>
                  setVehicle({
                    ...vehicle,
                    coverType: e.target.value as typeof vehicle.coverType,
                  })
                }
              >
                <option value="COMPREHENSIVE">Comprehensive</option>
                <option value="THIRD_PARTY">Third Party Only</option>
              </select>
            </Field>
            {vehicle.coverType === "COMPREHENSIVE" && (
              <Field id="vehicleValueKes" label="Value of Vehicle (KES)" error={errors.vehicleValueKes} required>
                <input
                  id="vehicleValueKes"
                  type="number"
                  value={vehicle.vehicleValueKes || ""}
                  onChange={(e) =>
                    setVehicle({ ...vehicle, vehicleValueKes: Number(e.target.value) })
                  }
                  onBlur={() => touch("vehicleValueKes")}
                />
              </Field>
            )}
            <div className="fa-grid fa-grid-cols-1 fa-md:grid-cols-2 fa-gap-3">
              <Field id="registrationNumber" label="Vehicle Registration Number" error={errors.registrationNumber} required>
                <input
                  id="registrationNumber"
                  value={vehicle.registrationNumber}
                  onChange={(e) =>
                    setVehicle({
                      ...vehicle,
                      registrationNumber: e.target.value.toUpperCase(),
                    })
                  }
                  onBlur={() => touch("registrationNumber")}
                />
              </Field>
              <Field id="yearOfManufacture" label="Vehicle's Year of Manufacture" error={errors.yearOfManufacture ?? vehicleAgeError ?? undefined} required>
                <input
                  id="yearOfManufacture"
                  type="number"
                  value={vehicle.yearOfManufacture}
                  onChange={(e) => {
                    setVehicleAgeError(null);
                    setVehicle({ ...vehicle, yearOfManufacture: Number(e.target.value) });
                  }}
                  onBlur={() => touch("yearOfManufacture")}
                />
                <p className="fa-text-xs fa-text-slate-500 fa-mt-1">
                  Maximum vehicle age 15 years and below. Vehicles above 15 years can be considered on mechanical evaluation.
                </p>
              </Field>
              <Field id="make" label="Make of Vehicle" error={errors.make} required>
                <input id="make" value={vehicle.make} onChange={(e) => setVehicle({ ...vehicle, make: e.target.value })} onBlur={() => touch("make")} />
              </Field>
              <Field id="model" label="Model of Vehicle" error={errors.model} required>
                <input id="model" value={vehicle.model} onChange={(e) => setVehicle({ ...vehicle, model: e.target.value })} onBlur={() => touch("model")} />
              </Field>
            </div>
          </div>
        </div>
      )}

      {wizard.currentStep.key === "extensions" && (
        <div className="fa-step-enter" key="extensions">
          <div className="fa-section fa-section-orange fa-space-y-3">
            {(
              [
                ["excessProtector", '"Excess Protector" Extension'],
                ["politicalViolenceAndTerrorism", '"Political Violence and Terrorism" Extension'],
                ["aaRoadRescue", '"AA of Kenya Road Rescue" Extension'],
                ["personalAccident", "Personal Accident Cover for Insured/Authorized Driver"],
                ["courtesyCar", '"Courtesy Car/Loss of Use" Extension'],
              ] as const
            ).map(([key, label]) => (
              <Field key={key} id={`ext-${key}`} label={`Include the ${label}?`}>
                <select
                  id={`ext-${key}`}
                  value={ext[key] ? "YES" : "NO"}
                  onChange={(e) => setExt({ ...ext, [key]: e.target.value === "YES" })}
                >
                  <option value="YES">YES</option>
                  <option value="NO">NO</option>
                </select>
              </Field>
            ))}
            <Field id="coverStartDate" label="Cover Start Date" error={errors.coverStartDate} required>
              <input
                id="coverStartDate"
                type="date"
                value={vehicle.coverStartDate}
                min={todayIso()}
                onChange={(e) => setVehicle({ ...vehicle, coverStartDate: e.target.value })}
                onBlur={() => touch("coverStartDate")}
              />
            </Field>
          </div>
        </div>
      )}

      {wizard.currentStep.key === "review" && (
        <div className="fa-step-enter" key="review">
          <ReviewSummary
            sections={[
              {
                title: "Member Details",
                fields: [
                  { label: "ICPAK Member Number", value: identity.icpakMemberNumber },
                  { label: "First Name", value: identity.firstName },
                  { label: "Last Name", value: identity.lastName },
                  { label: "Phone", value: identity.phone },
                  { label: "Email", value: identity.email },
                ],
              },
              {
                title: "Vehicle Details",
                fields: [
                  { label: "Cover Type", value: vehicle.coverType === "COMPREHENSIVE" ? "Comprehensive" : "Third Party Only" },
                  ...(vehicle.coverType === "COMPREHENSIVE"
                    ? [{ label: "Vehicle Value (KES)", value: vehicle.vehicleValueKes.toLocaleString() }]
                    : []),
                  { label: "Registration Number", value: vehicle.registrationNumber },
                  { label: "Year of Manufacture", value: String(vehicle.yearOfManufacture) },
                  { label: "Make", value: vehicle.make },
                  { label: "Model", value: vehicle.model },
                ],
              },
              {
                title: "Extensions & Cover",
                fields: [
                  { label: "Excess Protector", value: ext.excessProtector ? "Yes" : "No" },
                  { label: "Political Violence & Terrorism", value: ext.politicalViolenceAndTerrorism ? "Yes" : "No" },
                  { label: "AA Road Rescue", value: ext.aaRoadRescue ? "Yes" : "No" },
                  { label: "Personal Accident", value: ext.personalAccident ? "Yes" : "No" },
                  { label: "Courtesy Car", value: ext.courtesyCar ? "Yes" : "No" },
                  { label: "Cover Start Date", value: vehicle.coverStartDate },
                ],
              },
            ]}
          />
          {quote && (
            <>
              <RatesDisclaimer />
              <QuoteTable title="Comprehensive Motor Cover Premium" quote={quote} />
            </>
          )}
          <FormError message={submitError} />
        </div>
      )}

      <StepNavigation
        isFirstStep={wizard.isFirstStep}
        isLastStep={wizard.isLastStep}
        onBack={wizard.goBack}
        onNext={wizard.goNext}
        onSubmit={submitDetails}
        submitLoading={isSubmitting}
        submitDisabled={!isValid}
        submitLabel="Submit Car Details"
      />
    </div>
  );
}
